// A real league that has not drafted yet: the seats, how to fill them, and —
// since 2026-09-17 — how to leave one, hand it over, or run it
// (LEAGUE_SETUP.md "Invite widget", scenarios B1, B2 and J4).
//
// WHAT THIS REPLACES, AND WHY IT HAD TO. Until now every real league — one
// you created, one you joined with a code — rendered `LeagueNotConnected`:
// "your seat is real and saved, and there is nothing truthful to show you
// here yet." That was honest while nothing inside a league came from the
// database. It stopped being the best available answer the moment creating a
// league was real, because a commissioner who has just made one is not
// waiting for a lineup screen. They want the code, to send to four people.
//
// So this is the part of a league that IS truthful today: who is in, which
// seats are open, when the draft is, and the link. Everything it shows comes
// from `GET /api/leagues/:id/setup`. It does not pretend to be Home — Home
// has a matchup, a health bar and a pulse, none of which exist for a league
// that has never played — and the "what comes next" line says so plainly
// rather than leaving somebody to discover it by tapping.
//
// EVERY DESTRUCTIVE ACTION ASKS TWICE, in place: the button becomes "Sure?"
// and does the thing on the second tap. A modal would be the obvious choice
// and is worse here — it covers the seat list you are deciding about, and on a
// phone it arrives as a sheet over the one row you were looking at. The second
// tap is on the row itself, beside the name.

import { useEffect, useRef, useState } from 'react';
import {
  draftOrderWords,
  formatInZone,
  valueOf,
  whenInZone,
  type KeepersView,
  type LeagueSetupView,
  type SeatRef,
} from '@fsp/domain';
import { useReaderZone } from '../data/hooks';
import { Icon } from '../kit/Icon';
import { Panel } from '../kit/Panel';
import { Hint } from '../kit/overlays';
import {
  Button,
  Card,
  Caption,
  IconButton,
  Line,
  Stack,
  Text,
  TextField,
  Title,
  tell,
} from '../kit/ui';
import '../styles/screens/predraft.css';
import { leagueAction, seatAct, useLeagueSetup } from '../data/leagueSetup';
import { useKeepers } from '../data/keepers';
import { messageOf } from '../data/api';
import { periodName, useSeasonCalendar } from '../data/seasonCalendar';
import { Confirm } from './shared/Confirm';
import { EmailInvites } from './shared/EmailInvites';
import { NoCommissioner, SeatRow, YouPanel } from './shared/Seats';
import { SeatInviteSheet } from './shared/SeatSheets';
import { BotSeatSheet, FillSeatsSheet } from './shared/BotChooser';
import { NewToThis } from './shared/NewToThis';
import { useEditTeam } from '../shell/editTeam';
import { useDelight } from '../delight/useDelight';

/** One readable column (`fsp-predraft-column`), capped so a 1280 desktop does
    not stretch a seat list across the whole screen. */
const COLUMN = 'fsp-widgets fsp-predraft-column';

/** The draft, in words, in the reader's zone and named (B-181). */
function draftLine(view: LeagueSetupView, zone: string | null): string {
  if (view.draftAt === null) return 'No draft scheduled yet.';
  if (zone === null) return 'Draft scheduled.';
  const when = formatInZone(view.draftAt, zone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const type = view.draftType === 'auction' ? 'Auction' : draftOrderWords(view.draftType).label;
  return view.draftMode === 'manual'
    ? `${type} draft, in person, ${when}.`
    : view.draftMode === 'live'
      ? `${type} draft, live in the draft room, ${when}.`
      : `${type} draft ${when}.`;
}

/**
 * One seat before the draft, and what a commissioner may do to it here: open
 * a seat somebody is in, close one nobody is. The row is shared with Managers
 * (`shared/Seats.tsx`); the actions are this page's.
 */
function Seat({
  seat,
  fresh = false,
  canManage,
  busy,
  onOpen,
  onClose,
  onEdit,
  onInvite,
  onBot,
  onUnbot,
}: {
  seat: SeatRef;
  /** Somebody took it since the page last read the league: it lights up a moment (§3.3). */
  fresh?: boolean;
  canManage: boolean;
  busy: boolean;
  onOpen: (teamId: string) => void;
  onClose: (teamId: string) => void;
  /** Choose a bot for an open seat (AI_MANAGERS.md §3.1, B-459). */
  onBot: (teamId: string) => void;
  /** And take one off again: the seat is open under its placeholder. */
  onUnbot: (teamId: string) => void;
  /** A team brought in from a file (H10): its own link, the only way in. */
  onInvite: (teamId: string) => void;
  /** My own seat's name and colors (SCENARIOS L2). Offered on mine alone. */
  onEdit?: () => void;
}) {
  const empty = seat.ownerName === null;
  // A PENCIL, NOT A WORDED BUTTON: at 1280 the seats are three columns of
  // about 230px, and "Edit team" in words left the team's own name three
  // letters wide beside it. The label is the button's name for a screen reader
  // and its tooltip for a pointer.
  const edit =
    seat.isMine && onEdit ? (
      <IconButton icon="edit" label="Edit team" variant="secondary" onClick={onEdit} />
    ) : null;
  const actions = canManage ? (
    <div className="fsp-predraft-seat-actions">
      {edit}
      {seat.seat === 'manager' && !seat.isMine && seat.role !== 'commissioner' ? (
        <Confirm
          label="Open seat"
          confirmLabel="Open it?"
          busy={busy}
          onConfirm={() => onOpen(seat.teamId)}
        />
      ) : null}
      {/* A TEAM BROUGHT IN FROM A FILE is handed over by its own link, and
          closing its seat would take a team with a history out of the league. */}
      {empty && seat.named ? (
        <Button
          size="xs"
          variant={seat.invite !== null ? 'soft' : 'secondary'}
          onClick={() => onInvite(seat.teamId)}
        >
          {seat.invite !== null ? 'Link' : 'Invite'}
        </Button>
      ) : null}
      {/* A BOT FOR AN OPEN SEAT, chosen now rather than whoever is left on
          draft night (B-459); a bot's seat can be emptied again. An icon,
          as the pencil is: at 1280 a seat is a third of the width, and two
          worded buttons left its name no room at all. */}
      {empty && !seat.named && seat.seat !== 'bot' ? (
        <IconButton
          icon="bot"
          label="Put a bot in this seat"
          variant="secondary"
          onClick={() => onBot(seat.teamId)}
        />
      ) : null}
      {seat.seat === 'bot' ? (
        <Confirm
          label="Take off"
          confirmLabel="Take the bot off?"
          busy={busy}
          onConfirm={() => onUnbot(seat.teamId)}
        />
      ) : null}
      {empty && !seat.named && seat.seat !== 'bot' ? (
        <Confirm
          label="Close seat"
          confirmLabel="Close it?"
          busy={busy}
          onConfirm={() => onClose(seat.teamId)}
        />
      ) : null}
    </div>
  ) : edit ? (
    <div className="fsp-predraft-seat-actions">{edit}</div>
  ) : null;
  const row = <SeatRow seat={seat} actions={actions} />;
  return fresh ? <div className="fsp-seat-fresh">{row}</div> : row;
}

/**
 * When the draft is, what it will do, and the season it starts.
 *
 * SAID BEFORE IT HAPPENS, because it happens with nobody watching: the draft
 * runs itself at its time, every pick at once, and a manager who did not know
 * that finds out the next morning (CLAUDE.md, "say what will happen"). This
 * said "N seconds a pick" and that everything inside a league still read the
 * practice world until 2026-09-24 — a pick clock nothing runs, and a sentence
 * E12 had made untrue.
 */
function NextSteps({ view, hasSeat }: { view: LeagueSetupView; hasSeat: boolean }) {
  const zone = useReaderZone();
  const calendar = useSeasonCalendar(view.league.sport, view.league.season);
  const labels = calendar.snapshot.state === 'ready' ? calendar.snapshot.value : null;
  const season = view.regularSeason;
  if (view.draftMode === 'live') return <LiveNextSteps view={view} hasSeat={hasSeat} />;
  return (
    <Card padded>
      <Stack gap={2}>
        <Line>
          <span className="fsp-accent-text">
            <Icon name="when" />
          </span>
          <Text weight="strong">{draftLine(view, zone)}</Text>
        </Line>
        <Text tone="secondary" block>
          {view.draftMode === 'manual'
            ? view.canManage
              ? 'Draft together, in person or anywhere you like. Afterward, enter every pick on the draft page, in order. Rosters are set when you finish, and any open seat gets a bot.'
              : 'Draft together, in person or anywhere you like. Afterward, the commissioner enters every pick, and rosters are set when they finish.'
            : view.draftAt === null
              ? 'No draft time is set, so the draft will not run until one is.'
              : 'At that time the draft runs by itself, every pick at once, with nobody needing to be here. Open seats get a bot. Each team is picked from its manager’s priorities, then from the house ranking.'}
        </Text>
        {season !== null ? (
          <Text tone="secondary" block>
            {`The season is ${periodName(labels, season.first)} to ${periodName(labels, season.last)}, then the playoffs. Its schedule can be seen now, and is set when the draft is done.`}
          </Text>
        ) : null}
        <Line wrap>
          {view.draftMode === 'manual' ? (
            view.canManage ? (
              <Button to={`/l/${view.league.id}/draft`} variant="primary">
                Enter the draft
              </Button>
            ) : null
          ) : hasSeat ? (
            <Button to={`/l/${view.league.id}/priorities`} variant="primary">
              Set your draft priorities
            </Button>
          ) : null}
          <Button to={`/l/${view.league.id}/draft/mock`} variant="secondary">
            Try a mock draft
          </Button>
          <Button to={`/l/${view.league.id}/schedule`} variant="secondary">
            See the schedule
          </Button>
        </Line>
      </Stack>
    </Card>
  );
}

/**
 * A live draft's next steps (D10): when, how long a pick has, that the room is
 * open now, and the way in. While the draft is on, the way in is the whole
 * card — somebody who opened the league mid-draft is one tap from their pick.
 *
 * SAID BEFORE IT HAPPENS, like the autopick draft's: a manager who is not in
 * the room when their pick comes up is picked for, and that is the line to
 * read before draft day rather than after it (CLAUDE.md, "say what will
 * happen"). The rules page says exactly what the clock does when it runs out.
 */
function LiveNextSteps({ view, hasSeat }: { view: LeagueSetupView; hasSeat: boolean }) {
  const zone = useReaderZone();
  const on = view.draftStatus === 'live' || view.draftStatus === 'paused';
  const room = `/l/${view.league.id}/draft`;
  return (
    <Card padded>
      <Stack gap={2}>
        <Line>
          <span className="fsp-accent-text">
            <Icon name="when" />
          </span>
          <Text weight="strong">
            {on
              ? view.draftStatus === 'paused'
                ? 'The draft is paused.'
                : 'The draft is on.'
              : draftLine(view, zone)}
          </Text>
        </Line>
        <Text tone="secondary" block>
          {on
            ? 'Everyone is drafting in the draft room now.'
            : view.draftAt === null
              ? `No draft time is set. The commissioner starts the draft from the draft room, ${String(view.pickSeconds)} seconds a pick.`
              : `Everyone drafts together in the draft room, ${String(view.pickSeconds)} seconds a pick, and it starts at that time whoever is there. The room is open now, so you can line up a queue before it starts. ${
                  view.whenTheClockExpires === 'off'
                    ? 'If your clock runs out, the room waits for your pick.'
                    : 'If your clock runs out, the room picks for you, and if you are away it keeps picking for you until you turn auto off.'
                } Open seats get a bot.`}
        </Text>
        <Line wrap>
          <Button to={room} variant="primary">
            {on ? 'Join the draft' : 'Open the draft room'}
          </Button>
          {hasSeat && !on ? (
            <Button to={`/l/${view.league.id}/priorities`} variant="secondary">
              Set your priorities
            </Button>
          ) : null}
          {!on ? (
            <Button to={`/l/${view.league.id}/draft/mock`} variant="secondary">
              Try a mock draft
            </Button>
          ) : null}
          {!on ? (
            <Button to={`/l/${view.league.id}/schedule`} variant="secondary">
              See the schedule
            </Button>
          ) : null}
        </Line>
      </Stack>
    </Card>
  );
}

/** How often the page reads the league again while a seat waits for somebody (§3.3). */
const JOIN_ASK_MS = 15_000;
/** How long a seat somebody just took stays lit. */
const FRESH_MS = 2_500;

/**
 * JOINS AS THEY HAPPEN (PLAN-2026-10-28-by-email §3.3): while the page is on
 * screen and a seat has nobody in it, the league is read again every fifteen
 * seconds, and when the window comes back to the front. A league whose seats
 * are full stops asking. The socket a league page will one day hold (S-02) is
 * not built; a read of one league's seats is a few dozen rows.
 */
function useAskWhileWaiting(ask: () => void, waiting: boolean): void {
  useEffect(() => {
    if (!waiting) return;
    const now = () => {
      if (!document.hidden) ask();
    };
    const timer = window.setInterval(now, JOIN_ASK_MS);
    document.addEventListener('visibilitychange', now);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', now);
    };
  }, [ask, waiting]);
}

/**
 * The seats somebody took since the page last read the league, lit for a
 * moment, each said and celebrated once: *Dave joined. 6 of 8 in.* Not the
 * first read, and not my own seat, which I took myself.
 */
function useFreshJoins(view: LeagueSetupView | null): ReadonlySet<string> {
  const { play, celebrate } = useDelight();
  // WHO SAT WHERE AT THE LAST READ, OF THIS LEAGUE: switching to another
  // league's page starts again, or every seat of it would read as a join.
  const seen = useRef<{ leagueId: string; owners: Map<string, string | null> } | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  // THE LIGHT GOES OUT ON ITS OWN TIMER, not the effect's cleanup: a read
  // landing inside the moment would clear it and leave the seat lit.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );
  useEffect(() => {
    if (view === null) return;
    const before = seen.current?.leagueId === view.league.id ? seen.current.owners : null;
    seen.current = {
      leagueId: view.league.id,
      owners: new Map(view.seats.map((s) => [s.teamId, s.ownerName])),
    };
    if (before === null) return;
    const joined = view.seats.filter(
      (s) => s.ownerName !== null && (before.get(s.teamId) ?? null) === null && !s.isMine,
    );
    if (joined.length === 0) return;
    const filled = view.seats.filter((s) => s.ownerName !== null).length;
    const names = joined.map((s) => s.ownerName!).join(' and ');
    tell(`${names} joined. ${String(filled)} of ${String(view.seats.length)} in.`, 'good');
    play('claimWon');
    celebrate(
      joined.flatMap((s) => [s.colors.primary, s.colors.secondary]),
      1200,
    );
    setFresh(new Set(joined.map((s) => s.teamId)));
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFresh(new Set()), FRESH_MS);
  }, [view, play, celebrate]);
  return fresh;
}

export function LeaguePreDraftScreen({ leagueId }: { leagueId: string }) {
  const { snapshot, reload } = useLeagueSetup(leagueId);
  const held =
    snapshot.state === 'ready'
      ? snapshot.value
      : snapshot.state === 'failed'
        ? snapshot.lastValue
        : null;
  useAskWhileWaiting(reload, held !== null && held.seats.some((s) => s.ownerName === null));
  const fresh = useFreshJoins(held);
  const editTeam = useEditTeam();
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  // The team whose own link is open (H10).
  const [inviting, setInviting] = useState<string | null>(null);
  // The seat a bot is being chosen for, and whether every open seat is (B-459).
  const [botSeat, setBotSeat] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);
  // "Copied" on the Copy button for a moment after it worked, then back.
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // `loading` is distinct from "there is nothing here", and this is the
  // branch that keeps it that way. Drawing an empty seat list on the first
  // render would tell a commissioner their league is empty.
  if (snapshot.state === 'loading') {
    return (
      <div className="fsp-screen">
        <div className={COLUMN}>
          <Panel title="League">
            <Text tone="secondary">Loading your league…</Text>
          </Panel>
        </div>
      </div>
    );
  }

  // A refresh that failed keeps what it had — `lastValue` — because a
  // commissioner mid-invite should not lose the page over one bad request.
  // With nothing held, this is the only thing to show, and it shows the
  // failure's own sentence rather than a generic one.
  const view = snapshot.state === 'ready' ? snapshot.value : snapshot.lastValue;
  if (view === null) {
    return (
      <div className="fsp-screen">
        <div className={COLUMN}>
          <Panel title="League">
            <Stack gap={2}>
              <Text block>
                {snapshot.state === 'failed'
                  ? snapshot.failure.message
                  : 'Could not load that league.'}
              </Text>
              <Line>
                <Button onClick={reload}>Try again</Button>
              </Line>
            </Stack>
          </Panel>
        </div>
      </div>
    );
  }

  const filled = view.seats.filter((s) => s.ownerName !== null).length;
  // Open to a bot: nobody in it, no bot in it, not a team brought in from a file.
  const openToBots = view.seats.filter(
    (s) => s.ownerName === null && s.seat !== 'bot' && !s.named,
  ).length;
  const unbot = async (teamId: string) => {
    setBusy(true);
    const reply = await seatAct(view.league.id, teamId, 'unbot');
    setBusy(false);
    if (reply.status === 'ok') {
      tell('The seat is open again.', 'good');
      reload();
      return;
    }
    tell(
      messageOf(reply, 'That did not work.', 'Could not reach the site. Try again.'),
      reply.status === 'refused' ? 'warn' : 'bad',
    );
  };
  const link = `${window.location.origin}/join/${view.inviteCode}`;
  const mySeat = view.seats.find((s) => s.isMine) ?? null;

  /**
   * Run one of the league's actions and say what happened.
   *
   * A refusal is the SERVER'S sentence, shown as written. These are the
   * refusals somebody actually needs — "you run this league, hand it over
   * first", "somebody is in that seat" — and replacing them with a generic
   * failure would throw away the only part that tells them what to do next.
   */
  const act = async (path: string, body: unknown, done: string) => {
    setBusy(true);
    const result = await leagueAction(view.league.id, path, body);
    setBusy(false);
    if (result.status === 'ok') {
      tell(done, 'good');
      reload();
      return;
    }
    tell(
      messageOf(result, 'That did not work.', 'Could not reach the site. Try again.'),
      result.status === 'refused' ? 'warn' : 'bad',
    );
  };

  /**
   * Share where a phone can, copy where it cannot, and SAY SO EITHER WAY.
   *
   * The first version was `void navigator.clipboard?.writeText(link).then(…)`
   * with no catch and no fallback, which does nothing at all — silently — in
   * two ordinary cases: `navigator.clipboard` is undefined (the optional
   * chain short-circuits the whole expression), and `writeText` rejects
   * because the document is not focused or permission was refused. Somebody
   * taps Share, nothing happens, and the one thing they came to this page for
   * is the link.
   *
   * The last resort selects the field instead, because a link you can copy by
   * hand beats a button that lies. The code is on screen in large type
   * regardless, which is the real fallback.
   */
  const share = () => {
    const nav = navigator as Navigator & {
      share?: (d: { title: string; url: string }) => Promise<void>;
    };
    const copied = () => {
      setShared(true);
      tell('Copied. Send it to the group chat.', 'good');
    };
    const byHand = () => {
      const field = document.querySelector<HTMLInputElement>('input[aria-label="Invite link"]');
      field?.select();
      tell('Could not copy it for you — the link is selected, copy it from there.', 'warn');
    };
    if (typeof nav.share === 'function') {
      // A dismissed share sheet rejects, and that is not a failure — the
      // person changed their mind. Falling back to a copy would be wrong.
      void nav.share({ title: view.league.name, url: link }).catch(() => undefined);
      return;
    }
    if (!navigator.clipboard) {
      byHand();
      return;
    }
    void navigator.clipboard.writeText(link).then(copied, byHand);
  };

  /** The Copy button: the link on the clipboard, or selected to copy by hand. */
  const copy = () => {
    if (!navigator.clipboard) {
      document.querySelector<HTMLInputElement>('input[aria-label="Invite link"]')?.select();
      tell('Could not copy it for you — the link is selected, copy it from there.', 'warn');
      return;
    }
    void navigator.clipboard.writeText(link).then(
      () => setCopied(true),
      () => {
        document.querySelector<HTMLInputElement>('input[aria-label="Invite link"]')?.select();
        tell('Could not copy it for you — the link is selected, copy it from there.', 'warn');
      },
    );
  };

  return (
    <div className="fsp-screen">
      {/* ONE grid item, not three. `.fsp-widgets` is the dashboard grid, and
          three sibling panels in it get rows sized from the CONTAINER rather
          than from their content — so the seat list rendered 577px of seats
          inside a 233px row and, because a phone panel has `overflow:
          visible`, drew the back half straight through the panel below it.
          Found at 390 in a screenshot; every measurement said the page did
          not scroll, which it did not, and it was still wrong. The Appearance
          screen already uses this shape. */}
      <div className={COLUMN}>
        <Stack gap={4}>
          {/* A refresh that failed is NEVER silent. The page below is the last
            good answer, and a commissioner acting on it — opening a seat,
            rotating the code — needs to know it may have moved since. Showing
            stale data with no notice is the failure mode `lastValue` would
            otherwise introduce. */}
          {snapshot.state === 'failed' ? (
            <Card padded>
              <Stack gap={2}>
                <Text block>{snapshot.failure.message}</Text>
                <Line>
                  <Button size="xs" onClick={reload}>
                    Try again
                  </Button>
                </Line>
              </Stack>
            </Card>
          ) : null}
          {/* The screen names its own league, and it still should: cohesion
            check 1 in SCENARIOS.md is "you always know where you are: which
            league, which period, which team". The top bar answers it too as
            of 2026-09-17 — the switcher reads the id in the URL rather than
            the simulator's, so a real league is named there as well — but the
            top bar is not on screen at every width, and a page that says what
            it is about is right either way. */}
          <div>
            <Title>{view.league.name}</Title>
            <Text tone="secondary" block>
              {view.league.season} · {view.seats.length} teams · not drafted yet
            </Text>
          </div>

          {view.needsCommissioner ? (
            <NoCommissioner view={view} busy={busy} act={(p, b, d) => void act(p, b, d)} />
          ) : null}

          <Panel title="Invite" meta={`${filled} of ${view.seats.length} in`}>
            {/* A LEAGUE BROUGHT IN FROM A FILE (H10, B-454) whose open teams all
                wait for their own managers: the code seats nobody on one, so
                the panel says to send each team's own link instead. */}
            {view.seats.some((s) => s.ownerName === null) &&
            view.seats.filter((s) => s.ownerName === null).every((s) => s.named) ? (
              <Text tone="secondary" block>
                Every open team here came in from a file with its history, and waits for its own
                manager. Send each one its team’s link with Invite under Seats; the league’s code
                seats nobody on a team brought in.
              </Text>
            ) : (
              <Stack gap={2}>
                <Text tone="secondary" block>
                  Send this to everybody who is playing. They do not need an account first — the
                  link tells them which league it is.
                </Text>
                <Line>
                  <TextField
                    label="Invite link"
                    hideLabel
                    grow
                    readOnly
                    value={link}
                    onChange={() => {}}
                  />
                  <Hint label={copied ? 'Copied' : 'Copy the link'}>
                    <Button variant={copied ? 'soft' : 'secondary'} onClick={copy}>
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </Hint>
                  <Button icon="share" onClick={share} label="Share the invite link">
                    {shared ? 'Copied' : 'Share'}
                  </Button>
                </Line>
                {/* The code on its own, large, because somebody reads it out across
              a table. No letter in it can be confused for a digit. */}
                <div className="fsp-predraft-code">
                  <Caption>Or say the code</Caption>
                  <div className="fsp-disp fsp-num fsp-predraft-code-text">{view.inviteCode}</div>
                </div>
                {view.canManage ? (
                  <Stack gap={1}>
                    <Line className="fsp-predraft-center">
                      <Confirm
                        label="New code"
                        confirmLabel="Replace it?"
                        tone="warn"
                        busy={busy}
                        onConfirm={() =>
                          void act(
                            '/invite-code',
                            undefined,
                            (view.invites ?? []).length > 0
                              ? 'New code. The old link and the invites sent by email stop working.'
                              : 'New code. The old link stops working.',
                          )
                        }
                      />
                    </Line>
                    {/* SAID BEFORE THE PRESS (CLAUDE.md): an emailed invite's
                        link carries the code, and its address is invited once. */}
                    {(view.invites ?? []).length > 0 ? (
                      <Text size="xs" tone="secondary" block center>
                        A new code ends the invites sent by email too, and those addresses can’t be
                        emailed again. Send them the new link yourself.
                      </Text>
                    ) : null}
                  </Stack>
                ) : null}
                {/* BY EMAIL, a commissioner's, before the draft
                    (PLAN-2026-10-28-by-email §3.1): the read carries the
                    pending list to a commissioner alone. */}
                {view.canManage && view.invites !== undefined ? (
                  <EmailInvites
                    leagueId={view.league.id}
                    invites={view.invites}
                    onChanged={reload}
                  />
                ) : null}
              </Stack>
            )}
          </Panel>

          {/* BEFORE THE SEATS (2026-09-24): the draft runs itself, and setting your
              priorities is the one thing to do before it. Below eight seat rows it
              was a screen and a half down on a phone. */}
          <Panel title="What happens next">
            <Stack gap={2}>
              <NextSteps view={view} hasSeat={mySeat !== null} />
              {view.canManage && view.draftStatus === 'scheduled' ? (
                // CHANGES BEFORE THE DRAFT (DIRECTION A17, B-431): the draft time
                // first, since draft night is what a family moves. Only while the
                // draft is still to come: once it starts, they hold.
                <Stack gap={2}>
                  <Text size="xs" tone="secondary" block>
                    You run this league. Until the draft, League settings can change the draft time,
                    the roster, scoring and waivers. The code, the seats and handing it over work
                    here.
                  </Text>
                  <Line gap={2} wrap>
                    <Button
                      to={`/l/${view.league.id}/settings?section=draft`}
                      size="xs"
                      icon="when"
                    >
                      Change the draft time
                    </Button>
                    <Button to={`/l/${view.league.id}/settings`} size="xs" variant="quiet">
                      League settings
                    </Button>
                  </Line>
                </Stack>
              ) : null}
            </Stack>
          </Panel>

          {/* KEEPERS, in a keeper league started from the last (Q7, B-533):
              the one thing each manager does before this draft; a dynasty's
              roster, carried whole (Q8, B-535). */}
          {view.league.lastSeasonId !== null ? <KeepersChore leagueId={view.league.id} /> : null}

          {/* LAST SEASON, for a season started from it (H6, B-530): its own
              pages, and the history this season carries on. */}
          {view.league.lastSeasonId !== null ? (
            <Panel title="Last season">
              <Stack gap={2}>
                <Text block>
                  The {view.league.season - 1} season is in the record book, with every page of it
                  still open.
                </Text>
                <Line gap={2} wrap>
                  <Button to={`/l/${view.league.lastSeasonId}`} size="xs" iconRight="next">
                    The {view.league.season - 1} season
                  </Button>
                  <Button
                    to={`/l/${view.league.id}/history`}
                    size="xs"
                    variant="quiet"
                    iconRight="next"
                  >
                    History and the record book
                  </Button>
                </Line>
              </Stack>
            </Panel>
          ) : null}

          {/* FOR SOMEBODY WHO HAS NEVER PLAYED (B-444), until they put it away;
              a dynasty's draft adds to the rosters it carried (B-535). */}
          {view.league.lastSeasonId !== null ? (
            <CarriedNewToThis view={view} />
          ) : (
            <NewToThis league={view.league} auction={view.draftType === 'auction'} />
          )}

          <Panel title="Seats" meta={`${filled} of ${view.seats.length}`}>
            {/* EVERY OPEN SEAT AT ONCE (B-459), while there are some. */}
            {view.canManage && openToBots > 0 ? (
              <div className="fsp-predraft-fill">
                <Button size="xs" variant="secondary" icon="bot" onClick={() => setFilling(true)}>
                  {openToBots === 1
                    ? 'A bot for the open seat'
                    : `Bots for the ${String(openToBots)} open seats`}
                </Button>
              </div>
            ) : null}
            {/* ROOMY (B-459): with a bot to put in an open seat beside closing it,
                three across left the seat's own words no room. */}
            <div className="fsp-seats fsp-seats-roomy">
              {view.seats.map((seat) => (
                <Seat
                  key={seat.teamId}
                  seat={seat}
                  fresh={fresh.has(seat.teamId)}
                  canManage={view.canManage}
                  busy={busy}
                  onEdit={editTeam.open}
                  onOpen={(teamId) =>
                    void act(`/seats/${encodeURIComponent(teamId)}/open`, undefined, 'Seat opened.')
                  }
                  onClose={(teamId) =>
                    void act(
                      `/seats/${encodeURIComponent(teamId)}/close`,
                      undefined,
                      'Seat closed. The league is one smaller.',
                    )
                  }
                  onInvite={setInviting}
                  onBot={setBotSeat}
                  onUnbot={(teamId) => void unbot(teamId)}
                />
              ))}
            </div>
          </Panel>
          <BotSeatSheet
            leagueId={leagueId}
            teamId={botSeat}
            onClose={() => setBotSeat(null)}
            onView={() => reload()}
          />
          <FillSeatsSheet
            leagueId={leagueId}
            open={openToBots}
            opened={filling}
            onClose={() => setFilling(false)}
            onView={() => reload()}
          />
          <SeatInviteSheet
            view={view}
            leagueId={leagueId}
            teamId={inviting}
            onClose={() => setInviting(null)}
            onView={() => reload()}
          />

          <YouPanel view={view} busy={busy} act={(p, b, d) => void act(p, b, d)} drafted={false} />
        </Stack>
      </div>
    </div>
  );
}

/**
 * A keeper league's chore before its draft (Q7, B-533): how many my team has
 * chosen and when choices lock, and the way to the Keepers screen. Nothing in
 * a league that keeps nobody.
 */
function KeepersChore({ leagueId }: { leagueId: string }) {
  const zone = useReaderZone();
  const view = valueOf(useKeepers());
  if (view === null) return null;
  if (view.mode === 'dynasty') return <DynastyRoster view={view} />;
  if (view.mode !== 'keeper') return null;
  const mine = view.teams.find((t) => t.team.isMine) ?? null;
  const when =
    view.deadline === null || zone === null ? null : whenInZone(view.deadline, zone, Date.now());
  const chosen = mine?.chosen.length ?? 0;
  return (
    <Panel title="Keepers">
      <Stack gap={2}>
        <Text block>
          {view.final
            ? 'The draft has started, so every team’s keepers are set.'
            : mine === null
              ? `Each team keeps up to ${view.count === 1 ? 'one player' : `${view.count} players`} from last season.`
              : `You have chosen ${chosen} of ${view.count}. ${when === null ? 'Keepers lock an hour before the draft, once it has a time.' : `Keepers lock ${when}.`}`}
        </Text>
        <div>
          <Button
            to={`/l/${leagueId}/keepers`}
            size="xs"
            variant={mine !== null && !view.final ? 'primary' : 'quiet'}
            iconRight="next"
          >
            {mine !== null && !view.final ? 'Choose your keepers' : 'Every team’s keepers'}
          </Button>
        </div>
      </Stack>
    </Panel>
  );
}

/**
 * A dynasty's roster before its draft (Q8, B-535): every team kept its whole
 * roster from last season, mine listed, and what the draft and a roster over
 * its size mean for it.
 */
function DynastyRoster({ view }: { view: KeepersView }) {
  const mine = view.teams.find((t) => t.team.isMine) ?? null;
  const last = view.league.season - 1;
  return (
    <Panel title="Your roster">
      <Stack gap={2}>
        <Text block>
          {mine === null
            ? `Every team kept its whole roster from the ${last} season.`
            : `Every team kept its whole roster from the ${last} season, and these are yours. The draft adds to them, and a team over its roster’s size lets players go before it can add one.`}
        </Text>
        {mine !== null && mine.players.length > 0 ? (
          <ul className="fsp-predraft-roster">
            {mine.players.map((p) => (
              <li key={p.player.id}>
                {p.player.name}{' '}
                <Caption>
                  {[p.player.positions[0], p.player.proTeam].filter(Boolean).join(' · ')}
                </Caption>
              </li>
            ))}
          </ul>
        ) : null}
      </Stack>
    </Panel>
  );
}

/** New to this? in a season started from the last: a dynasty's draft adds to its rosters. */
function CarriedNewToThis({ view }: { view: LeagueSetupView }) {
  const keepers = valueOf(useKeepers());
  return (
    <NewToThis
      league={view.league}
      auction={view.draftType === 'auction'}
      carried={keepers?.mode === 'dynasty'}
    />
  );
}
